import classNames from "classnames"
import React, { ComponentProps } from "react"

import Typography, { TypographySize } from "./Typography"
import { Color } from "../../../constants/V2/color"
import { formatPrice } from "../../../utils/price"
import { ProductPrice } from "../../../utils/shop"

type Props = {
  productPrices: ProductPrice
  onPromotion?: boolean
  priceFontWeight?: ComponentProps<typeof Typography>["weight"]
  originalPriceLabel?: string
  originalPriceColor?: Color
  className?: string
  subscriptClassName?: string
  originalPriceClassName?: string
  priceFontSize?: TypographySize
  superscriptFontsize?: TypographySize
  subscriptText?: string
  priceColor?: Color
  showDecimals?: boolean
  overrideShowDiscount?: boolean
  showSavingsPercentage?: boolean
  originalPricePosition?: "top" | "right"
  subscriptPosition?: "bottom" | "right"
}

const PriceDisplay = ({
  productPrices,
  onPromotion,
  className,
  subscriptClassName,
  originalPriceClassName,
  originalPriceLabel,
  originalPriceColor = Color.Charcoal,
  priceFontWeight = "book",
  priceFontSize = "body-md",
  superscriptFontsize = "subscript-sm",
  subscriptText,
  priceColor = Color.Charcoal,
  showDecimals = true,
  overrideShowDiscount = false,
  showSavingsPercentage = false,
  originalPricePosition = "top",
  subscriptPosition = "bottom",
}: Props) => {
  const displayPrice = productPrices
    ? formatPrice(productPrices.price, {
        shouldShowDecimals: showDecimals,
        hasThousandsSeparator: true,
      })
    : ""

  //TODO see if this should be default for all prices
  const savingsAmount = productPrices.originalPrice
    ? Number.parseFloat(productPrices.originalPrice) -
      Number.parseFloat(productPrices.price)
    : 0

  const percentageSaved =
    productPrices.originalPrice && savingsAmount > 0
      ? Math.round(
          (savingsAmount / Number.parseFloat(productPrices.originalPrice)) * 100
        )
      : 0

  const displayOriginalPrice = productPrices.originalPrice
    ? formatPrice(productPrices.originalPrice, {
        shouldShowDecimals: showDecimals,
        hasThousandsSeparator: true,
      })
    : ""

  return (
    <div
      className={classNames(
        "flex flex-col items-start lg:items-start",
        className
      )}
    >
      {onPromotion && originalPricePosition == "top" && (
        <div className="flex flex-row uppercase">
          {originalPriceLabel && (
            <Typography
              color={originalPriceColor}
              text={originalPriceLabel}
              font="grotesk"
              size={superscriptFontsize}
              weight="book"
              className={classNames(originalPriceClassName, "pr-4", {
                "opacity-40": !overrideShowDiscount,
              })}
            />
          )}
          <Typography
            color={originalPriceColor}
            text={
              overrideShowDiscount
                ? formatPrice(savingsAmount.toString())
                : displayOriginalPrice
            }
            font="grotesk"
            size={superscriptFontsize}
            weight="book"
            className={classNames(
              !overrideShowDiscount && "line-through opacity-40"
            )}
          />
        </div>
      )}
      <div className="flex items-center gap-8">
        <div className="flex items-center gap-4">
          <Typography
            color={priceColor}
            text={displayPrice}
            font="grotesk"
            size={priceFontSize}
            weight={priceFontWeight}
          />
          {onPromotion && originalPricePosition == "right" && (
            <div className="flex flex-row uppercase">
              {originalPriceLabel && (
                <Typography
                  color={originalPriceColor}
                  text={originalPriceLabel}
                  font="grotesk"
                  size={superscriptFontsize}
                  weight="book"
                  className={classNames(originalPriceClassName, "pr-4", {
                    "opacity-40": !overrideShowDiscount,
                  })}
                />
              )}
              <Typography
                color={originalPriceColor}
                text={
                  overrideShowDiscount
                    ? formatPrice(savingsAmount.toString())
                    : displayOriginalPrice
                }
                font="grotesk"
                size={superscriptFontsize}
                weight="book"
                className={classNames(
                  originalPriceClassName,
                  !overrideShowDiscount && "line-through opacity-40"
                )}
              />
            </div>
          )}
          {subscriptText && subscriptPosition === "right" && (
            <Typography
              color={priceColor}
              weight="book"
              font="grotesk"
              size={superscriptFontsize}
              text={subscriptText}
              className={classNames(
                subscriptClassName,
                "-mt-8 hidden text-left sm:block lg:mt-0"
              )}
            />
          )}
        </div>

        {showSavingsPercentage && savingsAmount > 0 && (
          <div className="mb-[2px] flex h-fit items-center justify-center rounded-24 bg-orange px-8 py-[2px]">
            <Typography
              text={`-${percentageSaved}%`}
              size="body-sm"
              font="grotesk"
              weight="book"
              color={Color.White}
            />
          </div>
        )}
      </div>
      {subscriptText && (
        <Typography
          color={priceColor}
          weight="book"
          font="grotesk"
          size={superscriptFontsize}
          text={subscriptText}
          className={classNames(
            subscriptClassName,
            { "sm:hidden": subscriptPosition === "right" },
            "-mt-8 text-left lg:mt-0"
          )}
        />
      )}
    </div>
  )
}

export default PriceDisplay
